import { changeUserAccessBody, type UserAccessStatus } from '@planuze/admin-contracts';
import { toast } from '@planuze/ui';
import { useUsersContent } from '../../../content/sections/users.js';
import { useBlockUser, useUnblockUser } from '../../../hooks/use-users.js';
import { ReasonDialog } from '../../reason-dialog.js';
import { userErrorMessage } from '../user-error.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly userId: string;
  readonly userEmail: string;
  readonly currentStatus: UserAccessStatus;
}

/** Modal único para as duas transições. O texto expõe a cobertura real do
 * enforcement antes da confirmação; não promete revogação cross-service. */
export function ChangeUserAccessModal({ open, onClose, userId, userEmail, currentStatus }: Props) {
  const copy = useUsersContent();
  const block = useBlockUser(userId);
  const unblock = useUnblockUser(userId);
  const blocking = currentStatus === 'active';
  const mutation = blocking ? block : unblock;

  const confirm = (reason: string): void => {
    mutation.mutate(reason, {
      onSuccess: (result) => {
        const message = blocking
          ? result.alreadyInState
            ? copy.changeAccessModal.alreadyBlocked
            : copy.changeAccessModal.blockedSuccess(result.revokedAuthSessions)
          : result.alreadyInState
            ? copy.changeAccessModal.alreadyActive
            : copy.changeAccessModal.unblockedSuccess;
        toast.success(message);
        onClose();
      },
      onError: (error) => toast.error(userErrorMessage(error, copy.errors)),
    });
  };

  return (
    <ReasonDialog
      open={open}
      onClose={onClose}
      title={
        blocking
          ? copy.changeAccessModal.blockTitle(userEmail)
          : copy.changeAccessModal.unblockTitle(userEmail)
      }
      description={
        blocking
          ? copy.changeAccessModal.blockDescription
          : copy.changeAccessModal.unblockDescription
      }
      confirmLabel={
        blocking ? copy.changeAccessModal.blockConfirm : copy.changeAccessModal.unblockConfirm
      }
      reasonLabel={copy.changeAccessModal.reasonLabel}
      destructive={blocking}
      pending={mutation.isPending}
      validate={(reason) => changeUserAccessBody.safeParse({ reason }).success}
      onConfirm={confirm}
    />
  );
}
